import React from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import Image from 'next/image'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import Accordion from '../src/components/Accordion'
import WalletGuide from '../src/components/Product/wallets/WalletGuide'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
}))

const howCustodialWalletsWork = [
  {
    summary: 'Download wallet',
    details: 'If the wallet is a mobile app starty by downloading the app from the app store.',
    action: {
      label: 'Compare custodial wallets',
      link: urls.custodialWallets,
    },
  },
  {
    summary: 'Register account',
    details: 'Register an account by signing-up with your e-mail, password, address etc.',
  },
  {
    summary: 'Verify identity',
    details:
      'Most wallets need to verify your identity to comply with anti-money laundering regulations. This means you have to upload a copy of your passport to verify your identity.',
  },
  {
    summary: 'Buy crypto',
    details: 'You can buy crypto assets inside the wallet with a credit card or bank account.',
  },
  {
    summary: 'Invest your crypto',
    details:
      'Some custodial wallets also allow you to use your crypto assets productively by lending them and earning interest.',
  },
]

const howNonCustodialWalletsWork = [
  {
    summary: 'Download wallet',
    details:
      'Most non-custodial wallets are either mobile applications or browser extensions that you have to download.',
    action: {
      label: 'Compare non-custodial wallets',
      link: urls.nonCustodialWallets,
    },
  },
  {
    summary: 'Backup secret phrase',
    details:
      'Non-custodial wallets give you ownership over your private keys controlling your blockchain accounts. Therefore it’s important to backup your secret phrase in a safe manner as it grants you continued access to your funds in case you lose your phone or computer.',
  },
  {
    summary: 'Fund wallet',
    details:
      'In order to start using blockchain applications you will need to fund your wallet with Ether to pay for transaction fees on the Blockchain. This is best done by transfering from a crypto exchange.',
  },
  {
    summary: 'Start using',
    details:
      'Start exploring the world of DeFi, NFT’s and Gaming. If you don’t know where to start check out our academy.',
  },
]

const faqs = [
  {
    question: 'What is a crypto wallet?',
    answer:
      'A crypto wallet is a piece of software that enables you to communicate with Blockchain networks in order to send and receive cryptocurrencies. At its core, a wallet consists of a public key, which is your personal identifier, and a private key, which allows you to sign transactions and authenticate yourself to the network.',
  },
  {
    question: 'Is a custodial wallet safer than a non-custodial wallet?',
    answer:
      'There is no clear cut answer to what is safer. Both carry different trust assumptions. With a custodial wallet you trust a company to be honest, diligent and take the security of your funds seriously. With a non-custodial wallet you trust yourself and your ability to backup your seed phrase safely without other people having access to it.',
  },
  {
    question: 'What is a hardware wallet?',
    answer:
      'A hardware wallet is a device you use in combination with a non-custodial wallet app. Instead of storing the private keys on your device (mobile or computer) the private keys are stored in a small highly secured USB-shaped stick which you need to connect to your app in order to sign transactions. Some people like the security it gives you as a computer or phone can be hacked but a hardware wallet can’t.',
  },
  {
    question: 'Can crypto wallets be hacked?',
    answer:
      'Both non-custodial and custodial wallets can be “hacked” although it’s unlikely to happen. A non-custodial wallet can be breached if a hacker gets access to your secret phrase, either the encrypted version on your computer or the physical paper backup you have stored somewhere. A custodial wallet provider can be hacked if a hacker manages to get access to the internal security systems and transfer funds out of their centrally managed wallet.',
  },
  {
    question: 'Can crypto wallets be tracked?',
    answer:
      'A common misconception is that crypto transitions are anonymous and that there is no way for governments to find out whose identity is behind a wallet. This is wrong. A blockchain is a public ledger where every transaction can be traced back to its origin. In most cases the funds originated from a centralized exchange in which case there will be know identity since users have to verify themselves.',
  },
]

const Wallets = (): JSX.Element => {
  const classes = useStyles({})
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - Wallets`}
            description={`Cryptotesters.com.`}
            openGraph={{
              url: `https://cryptotesters.com`,
              title: `Cryptotesters.com - Wallets`,
              description: `Wallets Cryptotesters.com.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/home.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/home.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">Choosing the best wallet</Typography>
                  <Typography variant="body2">
                    A crypto wallet allows users to hold, buy, send and receive cryptocurrencies and
                    tokens. There are different variants of crypto wallets - the biggest distinction
                    is whether a wallet is custodial or non-custodial.
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    href={urls.custodialWallets}
                    className={classes.heroCTA}
                  >
                    Learn more about wallets
                  </Button>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <Image src="/wallet.png" objectFit="contain" layout="fill" />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
            <Grid container className={classes.section}>
              <Grid item md={6} xs={12}>
                <Typography variant="h1">Custodial Wallets</Typography>
                <Typography variant="body2">
                  Custodial wallets hold crypto funds on behalf of their clients. They are
                  equivalent to a bank account in the crypto world.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  href={urls.custodialWallets}
                  className={classes.heroCTA}
                >
                  Compare custodial wallets
                </Button>
                {/* <Button color="inherit" href="#" variant="text" className={classes.heroCTA}>
                  Video: Custodial wallets
                </Button> */}
                <Typography variant="h3">How do custodial wallets work?</Typography>
                <Accordion steps={howCustodialWalletsWork} />
              </Grid>

              <Grid item md={6} xs={12}>
                <div className={classes.videoWrapper}>
                  <Image src="/vault.png" objectFit="contain" layout="fill" />
                </div>
              </Grid>
            </Grid>

            <Grid container className={classes.section}>
              <Grid item md={6} xs={12}>
                <Typography variant="h1">Non-custodial wallets</Typography>
                <Typography variant="body2">
                  Non-custodial wallets put users in control of their funds. The wallet provider has
                  no means of accessing or freezing users funds.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  href={urls.nonCustodialWallets}
                  className={classes.heroCTA}
                >
                  Compare non-custodial wallets
                </Button>
                {/* <Button color="inherit" href="#" variant="text" className={classes.heroCTA}>
                  Video: Non-custodial wallets
                </Button> */}
                <Typography variant="h3">How do non-custodial wallets work?</Typography>
                <Accordion steps={howNonCustodialWalletsWork} />
              </Grid>

              <Grid item md={6} xs={12}>
                <div className={classes.videoWrapper}>
                  <Image src="/phone.png" objectFit="contain" layout="fill" />
                </div>
              </Grid>
            </Grid>

            <div>
              <WalletGuide />
              <FAQSection faqs={faqs} />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </>
      )}
    </Amplitude>
  )
}

export default Wallets
